import { Box, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { AppointmentOrderItem, CustomOrderItem, OrderItems, OrderizableTypes } from '@spike/order-model';
import Pet from '@spike/pet-model';
import clsx from 'clsx';
import AppointmentOptions from 'components/CreateBooking/MultiplePets/AppointmentOptions';
import uniqWith from 'lodash/uniqWith';
import moment, { Moment } from 'moment-timezone';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import AppointmentItem from './AppointmentItem';
import Date from './Date';
import OrderItem from './OrderItem';
import PetInfo from './PetInfo';
import { AlertNewBooking } from 'components/CreateBooking/UI/AlertNewBooking';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { useMarketplace, useTimeZone } from 'hooks';
import { PetVaccine } from '@spike/medical-history-model';
import { EvaluationNotification } from 'components/Evaluation/EvaluationNotification';
import { EvaluationReport } from '@spike/report-model';
import { useSelector } from 'react-redux';
import { dispatch, RootState } from 'store';
import Service from 'model/Service';
import { fetchEvaluationServiceThunk } from 'actions/services/ServicesActions';
import LatePickUpAlert from '../UI/LatePickUpAlert/LatePickUpAlert';
import { isEvaluationEnabled } from 'utils/MarketingUtils';
import { BoardingMetadata, DaycareMetadata } from '@spike/appointment-model';
import CheckInOutReport, { CheckInOutReportButton } from '../CheckInOutReport/CheckInOutReport';

// An evaluation category can cover more than one business area: dental services are evaluated
// under grooming, same grouping AppointmentItem uses for standard reports.
const businessAreasByEvaluationCategory: Record<string, Array<string>> = {
    grooming: ['grooming', 'dental'],
    daycare: ['daycare'],
    boarding: ['boarding']
};

export interface PetDetailProps {
    pet: Pet;
    orderItems: OrderItems;
    editable: boolean;
    editingOrder: boolean;
    onlyOnePet: boolean;
    isEvaluation?: boolean;
    readOnly?: boolean;
    className?: string;
    disableDeleteService?: boolean;
    savingAppointmentId?: number;
    resultsDrawerZIndex?: number;
    withoutOptions?: boolean;
    suppressAppointmentOptions?: boolean;
    canEditAppointmentItem?: (appointmentId: number, statusId: number) => boolean;
    onDeletePet?: () => void;
    onEdit?: (orderItemId: number) => void;
    onAdd?: (orderItemType: OrderizableTypes) => void;
    onDelete?: (orderItemId: number) => void;
    onViewReport?: (
        appointmentId: number,
        businessAreaId: string,
        serviceId?: number,
        pet?: Pet,
        evaluationReportId?: number
    ) => void;
    onAddReport?: (appointmentId: number, businessAreaId: string, serviceId?: number, pet?: Pet) => void;
    onStartSavingStatus?: (appointmentId: number) => void;
    onStopSavingStatus?: (appointmentId: number) => void;
    onRebook?: (appointmentItem: AppointmentOrderItem) => void;
    onShowPetCard?: (optSelected?: string) => void;
    onAddEvaluation?: (petId: number, orderItemType: OrderizableTypes) => void;
    onViewResults?: (evaluationReport?: EvaluationReport) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            border: '1px solid #D4D4D4',
            paddingLeft: 15,
            paddingTop: 15,
            paddingBottom: 15,
            borderRadius: 10
        },
        petInfo: {
            paddingBottom: 15,
            marginRight: 15
        },
        bottomLine: {
            borderBottom: '1px solid #D4D4D4'
        },
        addDropdown: {
            borderTop: 'none'
        },
        date: {
            borderTop: 'none',
            marginRight: 15
        },
        alert: {
            marginBottom: 20,
            minHeight: 39,
            padding: '5px 14px',
            borderRadius: 10,
            alignContent: 'center'
        },
        alertText: {
            fontWeight: 400,
            fontSize: 14,
            lineHeight: '120%',
            paddingTop: 2
        },
        update: {
            fontWeight: 600,
            cursor: 'pointer',
            textDecoration: 'underline'
        },
        updateDisabled: {
            fontWeight: 600,
            cursor: 'default',
            textDecoration: 'none'
        },
        bold: {
            fontWeight: 600
        },
        alertContainer: {
            marginRight: 15
        },
        alertActionsContainer: {
            display: 'flex',
            gap: '10px',
            marginTop: '8px'
        },
        alertButton: {
            'fontWeight': 600,
            'cursor': 'pointer',
            'textDecoration': 'underline',
            'fontSize': 14,
            'color': '#EF4F57',
            '&:hover': {
                opacity: 0.8
            }
        }
    })
);

const getDates = (orderItems: OrderItems) => {
    const appointmentDates = orderItems.appointments.map(appointmentItem =>
        appointmentItem.appointment.duration.from.clone().startOf('day')
    );

    const addOnServiceDates = orderItems.addOnServices
        .filter(addOnServiceItem => addOnServiceItem.orderItem.startedAt !== undefined)
        .map(addOnServiceItem => addOnServiceItem.orderItem.startedAt!.clone().startOf('day'));

    const productDates = orderItems.products
        .filter(productItem => productItem.orderItem.startedAt !== undefined)
        .map(productItem => productItem.orderItem.startedAt!.clone().startOf('day'));

    const customDates = orderItems.customs
        .filter(customItem => customItem.orderItem.startedAt !== undefined)
        .map(customItem => customItem.orderItem.startedAt!.clone().startOf('day'));

    return uniqWith([...appointmentDates, ...addOnServiceDates, ...productDates, ...customDates], (date, otherDate) =>
        date.isSame(otherDate)
    ).sort((date, otherDate) => date.diff(otherDate, 'days'));
};

const getAppointmentsByDate = (orderItems: OrderItems, date: Moment) =>
    orderItems.appointments.filter(appointmentItem =>
        appointmentItem.appointment.duration.from.clone().startOf('day').isSame(date)
    );

const getAddOnServicesByDate = (orderItems: OrderItems, date?: Moment) =>
    orderItems.addOnServices.filter(
        addonService => addonService.orderItem.startedAt?.clone().startOf('day').isSame(date) ?? date === undefined
    );

const getProductsByDate = (orderItems: OrderItems, date?: Moment) =>
    orderItems.products.filter(
        product => product.orderItem.startedAt?.clone().startOf('day').isSame(date) ?? date === undefined
    );

const getCustomsByDate = (orderItems: OrderItems, date?: Moment) =>
    orderItems.customs.filter(
        custom => custom.orderItem.startedAt?.clone().startOf('day').isSame(date) ?? date === undefined
    );

const getShowAlertLatePickUpFee = (orderItem: AppointmentOrderItem, timezone: string) => {
    const appointmentTo = moment.tz(orderItem.appointment.duration.to, timezone);
    const appointmentWithGrace = appointmentTo
        .clone()
        .add(
            (orderItem.appointment.serviceVariableDuration as DaycareMetadata | BoardingMetadata)?.latePickupFee
                ?.gracePeriod,
            'minutes'
        );
    const now = moment.tz(timezone);
    return now.isAfter(appointmentWithGrace);
};

export const PetDetail: FunctionComponent<PetDetailProps> = props => {
    const classes = useStyles();
    const timezone = useTimeZone();
    const marketplace = useMarketplace();
    const evaluationEnabled = isEvaluationEnabled(marketplace);
    const evaluationService = useSelector<RootState, Service | null | undefined>(
        state => state.services.evaluationService
    );

    const [savingStatusAppointmentId, setSavingStatusAppointmentId] = useState<number | undefined>();
    const [latePickupFee, setShowLatePickUpFee] = useState<boolean>(false);
    const [showCheckInOutReport, setShowCheckInOutReport] = useState(false);
    const isReadOnly = props.readOnly ?? false;
    const bookingId = props.orderItems.appointments[0]?.appointment.bookingId;
    const petId = props.pet.id;

    let appointmentPast = false;

    const dates = useMemo(() => getDates(props.orderItems), [props.orderItems]);

    const onlyOneAppointment = useMemo(() => props.orderItems.appointments.length === 1, [props.orderItems]);

    useEffect(() => {
        if (appointmentPast) {
            setShowLatePickUpFee(true);
        } else {
            setShowLatePickUpFee(false);
        }
    }, [appointmentPast]);

    useEffect(() => {
        // undefined means it was never fetched; null means the marketplace has no evaluation service.
        if (evaluationEnabled && evaluationService === undefined) {
            dispatch(fetchEvaluationServiceThunk());
        }
    }, [evaluationEnabled, evaluationService]);

    const startSavingStatusHandler = (appointmentId: number) => {
        setSavingStatusAppointmentId(appointmentId);
        props.onStartSavingStatus && props.onStartSavingStatus(appointmentId);
    };

    const stopSavingStatusHandler = (appointmentId: number) => {
        setSavingStatusAppointmentId(undefined);
        props.onStopSavingStatus && props.onStopSavingStatus(appointmentId);
    };

    const countExpiredVaccines = (): number => {
        if (!props.pet?.medicalHistory?.vaccines) return 0;
        const today = moment().tz(timezone).clone().startOf('day');

        const expiredVaccines: Array<PetVaccine> = [];

        props.pet?.medicalHistory?.vaccines.forEach(vaccine => {
            const vaccineFounded = expiredVaccines.find(v => v.type?.id === vaccine.type?.id);
            if (vaccineFounded) {
                if (vaccineFounded?.expiration?.isBefore(vaccine.expiration)) {
                    const index = expiredVaccines.indexOf(vaccineFounded);
                    expiredVaccines[index] = { ...vaccine };
                }
            } else {
                if (vaccine.status === 'expired') {
                    expiredVaccines.push({ ...vaccine });
                }
            }
        });

        props.pet?.medicalHistory?.vaccines.forEach(vaccine => {
            const vaccineFounded = expiredVaccines.find(v => v.type?.id === vaccine.type?.id);
            if (vaccineFounded?.expiration?.isBefore(vaccine.expiration)) {
                const index = expiredVaccines.indexOf(vaccineFounded);
                expiredVaccines[index] = { ...vaccine };
            }
        });

        props.pet?.medicalHistory?.vaccines.forEach(vaccine => {
            const vaccineFounded = expiredVaccines.find(v => v.type?.id === vaccine.type?.id);
            if (vaccineFounded?.expiration?.isBefore(vaccine.expiration)) {
                const index = expiredVaccines.indexOf(vaccineFounded);
                expiredVaccines[index] = { ...vaccine };
            }
        });

        return expiredVaccines.filter(vaccine => {
            if (!vaccine.expiration) return false;
            return vaccine.expiration.isSameOrBefore(today, 'day');
        }).length;
    };

    const haveEvaluationService = () => {
        if (!props.orderItems.appointments) {
            return false;
        }

        return props.orderItems.appointments.some(item => {
            return item?.appointment?.services?.some(service => {
                return service?.businessArea?.id === 'evaluation';
            });
        });
    };

    const handleAddEvaluation = () => {
        if (props.onAddEvaluation && props.pet.id && props.pet.clientId) {
            props.onAddEvaluation(props.pet.id, OrderizableTypes.APPOINTMENT);
        }
    };

    const handlerHideNotification = (): boolean => {
        const categories = evaluationService?.evaluation?.category;
        if (!categories || !props.orderItems.appointments) {
            return true;
        }

        const bookedBusinessAreas = new Set(
            props.orderItems.appointments.flatMap(item =>
                (item.appointment.services ?? []).map(service => service?.businessArea?.id)
            )
        );

        const someCategoryIsBooked = categories.some(category =>
            (businessAreasByEvaluationCategory[category] ?? []).some(area => bookedBusinessAreas.has(area))
        );

        return !someCategoryIsBooked;
    };

    return (
        <Box className={clsx(classes.container, props.className)}>
            {latePickupFee && <LatePickUpAlert />}
            <EvaluationNotification
                onAddEvaluation={handleAddEvaluation}
                onViewResults={props.onViewResults}
                isEvaluation={haveEvaluationService()}
                hideNotification={handlerHideNotification()}
                evaluationReport={props.pet.evaluationReport}
                readOnly={isReadOnly}
            />
            {countExpiredVaccines() > 0 && (
                <Box
                    className={classes.alertContainer}
                    onClick={!isReadOnly ? () => props.onShowPetCard && props.onShowPetCard('vaccines') : undefined}
                >
                    <AlertNewBooking
                        className={classes.alert}
                        bWidth="2px"
                        icon={faExclamationTriangle}
                        iconSize="lg"
                        iconColor="#EF4F57"
                        bgColor="#FBB9BC33"
                        bdColor="#EF4F57"
                    >
                        <Typography className={classes.alertText}>
                            {props.pet?.name} has <span className={classes.bold}>{countExpiredVaccines()}</span> expired
                            vaccine. Please review &{' '}
                            {isReadOnly ? (
                                <span className={classes.updateDisabled}>update</span>
                            ) : (
                                <a className={classes.update}>update</a>
                            )}
                        </Typography>
                    </AlertNewBooking>
                </Box>
            )}
            <PetInfo
                pet={props.pet}
                className={classes.petInfo}
                onDelete={props.editingOrder && !props.onlyOnePet ? props.onDeletePet : undefined}
                onShowPetCard={props.onShowPetCard}
                actions={
                    !props.editingOrder && bookingId && petId ? (
                        <CheckInOutReportButton onClick={() => setShowCheckInOutReport(true)} />
                    ) : undefined
                }
            />
            {showCheckInOutReport && bookingId && petId && (
                <CheckInOutReport
                    pet={props.pet}
                    petId={petId}
                    bookingId={bookingId}
                    orderItems={props.orderItems}
                    zIndex={props.resultsDrawerZIndex}
                    onClose={() => setShowCheckInOutReport(false)}
                />
            )}
            {dates.map((date, dateIndex) => {
                const appointmentItemsByDate = getAppointmentsByDate(props.orderItems, date);
                const addOnServiceItemsByDate = getAddOnServicesByDate(props.orderItems, date);
                const productItemsByDate = getProductsByDate(props.orderItems, date);
                const customItemsByDate = getCustomsByDate(props.orderItems, date);
                const appointmentPastDue = appointmentItemsByDate.some(
                    (appt: AppointmentOrderItem) =>
                        getShowAlertLatePickUpFee(appt, timezone) &&
                        (appt.appointment.serviceVariableDuration as DaycareMetadata | BoardingMetadata)?.latePickupFee
                            ?.enabled
                );
                appointmentPast = appointmentPastDue;

                return (
                    <>
                        <Date
                            date={date}
                            className={classes.date}
                        />
                        {appointmentItemsByDate.map((item, index) => (
                            <AppointmentItem
                                key={item.orderItem.id}
                                appointmentItem={item}
                                editable={props.editable}
                                hideEditWhenOrderFullyClosed={
                                    props.canEditAppointmentItem && item.appointment.id != null
                                        ? !props.canEditAppointmentItem(item.appointment.id, item.appointment.status.id)
                                        : false
                                }
                                withoutOption={
                                    props.withoutOptions || props.pet.deceased || !props.pet.active || props.pet.deleted
                                }
                                editingOrder={props.editingOrder}
                                savingOther={
                                    (savingStatusAppointmentId !== undefined &&
                                        item.appointment.id !== savingStatusAppointmentId) ||
                                    (props.savingAppointmentId !== undefined &&
                                        props.savingAppointmentId !== item.appointment.id)
                                }
                                className={
                                    appointmentItemsByDate.length - 1 > index ||
                                    addOnServiceItemsByDate.length > 0 ||
                                    productItemsByDate.length > 0 ||
                                    customItemsByDate.length > 0 ||
                                    (props.editable &&
                                        !props.pet.deceased &&
                                        props.pet.active &&
                                        !props.pet.deleted &&
                                        dateIndex + 1 === dates.length)
                                        ? classes.bottomLine
                                        : undefined
                                }
                                onEdit={() => props.onEdit && item.orderItem.id && props.onEdit(item.orderItem.id)}
                                onDelete={
                                    props.disableDeleteService || onlyOneAppointment
                                        ? undefined
                                        : () => props.onDelete && item.orderItem.id && props.onDelete(item.orderItem.id)
                                }
                                onRebook={() => props.onRebook && props.onRebook(item)}
                                onViewReport={props.onViewReport}
                                onAddReport={props.onAddReport}
                                onStartSavingStatus={() => startSavingStatusHandler(item.appointment.id ?? 0)}
                                onStopSavingStatus={() => stopSavingStatusHandler(item.appointment.id ?? 0)}
                                resultsDrawerZIndex={props.resultsDrawerZIndex}
                            />
                        ))}
                        {addOnServiceItemsByDate.map((item, index) => (
                            <OrderItem
                                key={item.orderItem.id}
                                type="Add-on"
                                orderItem={item.orderItem}
                                staff={item.staff}
                                editable={props.editable}
                                editingOrder={props.editingOrder}
                                withoutOptions={props.withoutOptions}
                                zIndex={props.resultsDrawerZIndex}
                                className={
                                    addOnServiceItemsByDate.length - 1 > index ||
                                    productItemsByDate.length > 0 ||
                                    customItemsByDate.length > 0 ||
                                    (props.editable &&
                                        !props.pet.deceased &&
                                        props.pet.active &&
                                        !props.pet.deleted &&
                                        dateIndex + 1 === dates.length)
                                        ? classes.bottomLine
                                        : undefined
                                }
                                onEdit={() => props.onEdit && item.orderItem.id && props.onEdit(item.orderItem.id)}
                                onDelete={() =>
                                    props.onDelete && item.orderItem.id && props.onDelete(item.orderItem.id)
                                }
                            />
                        ))}
                        {productItemsByDate.map((item, index) => (
                            <OrderItem
                                key={item.orderItem.id}
                                type="Product"
                                orderItem={item.orderItem}
                                staff={item.staff}
                                editable={props.editable}
                                editingOrder={props.editingOrder}
                                withoutOptions={props.withoutOptions}
                                zIndex={props.resultsDrawerZIndex}
                                className={
                                    productItemsByDate.length - 1 > index ||
                                    customItemsByDate.length > 0 ||
                                    (props.editable &&
                                        !props.pet.deceased &&
                                        props.pet.active &&
                                        !props.pet.deleted &&
                                        dateIndex + 1 === dates.length)
                                        ? classes.bottomLine
                                        : undefined
                                }
                                onEdit={() => props.onEdit && item.orderItem.id && props.onEdit(item.orderItem.id)}
                                onDelete={() =>
                                    props.onDelete && item.orderItem.id && props.onDelete(item.orderItem.id)
                                }
                            />
                        ))}
                        {customItemsByDate
                            .filter((custom: CustomOrderItem) => custom.orderItem.description !== 'Late PickUp Fee')
                            .map((item, index) => (
                                <OrderItem
                                    key={item.orderItem.id}
                                    type="Custom"
                                    orderItem={item.orderItem}
                                    staff={item.staff}
                                    editable={props.editable}
                                    editingOrder={props.editingOrder}
                                    withoutOptions={props.withoutOptions}
                                    zIndex={props.resultsDrawerZIndex}
                                    className={
                                        customItemsByDate.length - 1 > index ||
                                        (props.editable &&
                                            !props.pet.deceased &&
                                            props.pet.active &&
                                            !props.pet.deleted &&
                                            dateIndex + 1 === dates.length)
                                            ? classes.bottomLine
                                            : undefined
                                    }
                                    onEdit={() => props.onEdit && item.orderItem.id && props.onEdit(item.orderItem.id)}
                                    onDelete={() =>
                                        props.onDelete && item.orderItem.id && props.onDelete(item.orderItem.id)
                                    }
                                />
                            ))}
                    </>
                );
            })}
            {getAddOnServicesByDate(props.orderItems).map(item => (
                <OrderItem
                    key={item.orderItem.id}
                    type="Add-on"
                    orderItem={item.orderItem}
                    staff={item.staff}
                    editable={props.editable}
                    editingOrder={props.editingOrder}
                    withoutOptions={props.withoutOptions}
                    zIndex={props.resultsDrawerZIndex}
                    className={classes.bottomLine}
                    onEdit={() => props.onEdit && item.orderItem.id && props.onEdit(item.orderItem.id)}
                    onDelete={() => props.onDelete && item.orderItem.id && props.onDelete(item.orderItem.id)}
                />
            ))}
            {getProductsByDate(props.orderItems).map(item => (
                <OrderItem
                    key={item.orderItem.id}
                    type="Product"
                    orderItem={item.orderItem}
                    staff={item.staff}
                    editable={props.editable}
                    editingOrder={props.editingOrder}
                    withoutOptions={props.withoutOptions}
                    zIndex={props.resultsDrawerZIndex}
                    className={classes.bottomLine}
                    onEdit={() => props.onEdit && item.orderItem.id && props.onEdit(item.orderItem.id)}
                    onDelete={() => props.onDelete && item.orderItem.id && props.onDelete(item.orderItem.id)}
                />
            ))}
            {getCustomsByDate(props.orderItems).map(item => (
                <OrderItem
                    key={item.orderItem.id}
                    type="Custom"
                    orderItem={item.orderItem}
                    staff={item.staff}
                    editable={props.editable}
                    editingOrder={props.editingOrder}
                    withoutOptions={props.withoutOptions}
                    zIndex={props.resultsDrawerZIndex}
                    className={classes.bottomLine}
                    onEdit={() => props.onEdit && item.orderItem.id && props.onEdit(item.orderItem.id)}
                    onDelete={() => props.onDelete && item.orderItem.id && props.onDelete(item.orderItem.id)}
                />
            ))}
            {!props.pet.deceased &&
                props.pet.active &&
                !props.pet.deleted &&
                props.editable &&
                !props.withoutOptions &&
                !props.suppressAppointmentOptions && (
                    <AppointmentOptions
                        onAddService={() => props.onAdd && props.onAdd(OrderizableTypes.APPOINTMENT)}
                        onAddAddOn={() => props.onAdd && props.onAdd(OrderizableTypes.ADDON_SERVICE)}
                        onAddProduct={() => props.onAdd && props.onAdd(OrderizableTypes.PRODUCT)}
                        onAddCustomItem={() => props.onAdd && props.onAdd(OrderizableTypes.CUSTOM)}
                        className={classes.addDropdown}
                    />
                )}
        </Box>
    );
};

export default PetDetail;
